import * as React from 'react';

import { useIntl } from 'react-intl';

import imagePlaceholder from 'src/assets/images/placeholder.webp';
import ParkingImage from 'src/components/ParkingImage';
import ImageCarouselInModal from 'src/features/visitor/components/carousel/ImageCarouselInModal';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import {
    Carousel,
    type CarouselApi,
    CarouselContent,
    CarouselItem,
    CarouselItemSubtitle,
    CarouselItemTitle,
    CarouselNext,
    CarouselPrevious,
} from 'src/registry/ui/Carousel';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { ICON_MAP } from 'src/utils/constants';
import { commonMessages } from 'src/utils/messages';
import { cn } from 'src/utils/shadcn';

export type ImageSlide = {
    image: string | null;
    title: string | undefined;
    subtitle: string | null;
};

type ImageCarouselProps = {
    imageSlides?: ImageSlide[];
    className?: string;
    isLoading?: boolean;
};

const ImageCarousel = ({ imageSlides, className, isLoading }: ImageCarouselProps) => {
    const { formatMessage } = useIntl();
    const [api, setApi] = React.useState<CarouselApi>();
    const [selectedSlideImage, setSelectedSlideImage] = React.useState<ImageSlide | undefined>();
    const [currentIndex, setCurrentIndex] = React.useState<number>(0);

    const defaultImageSlides = [
        {
            image: imagePlaceholder,
            title: formatMessage(commonMessages.noImage),
            subtitle: '',
        },
    ];
    const hasAtLeastOneImage = !!imageSlides && imageSlides.length > 0;
    const hasAtLeastTwoImages = !!imageSlides && imageSlides.length > 1;
    const slidesToUse = hasAtLeastOneImage ? imageSlides : defaultImageSlides;

    // Keep the navigation title/subtitle (currentIndex) in sync with the slide embla is actually showing,
    // so they can never disagree when the user uses the arrows or drags. The modal is a separate viewer and
    // intentionally does NOT move this carousel - closing it leaves the panel on the slide it was showing
    React.useEffect(() => {
        if (!api) return;
        const syncIndex = () => setCurrentIndex(api.selectedScrollSnap());
        syncIndex();
        api.on('select', syncIndex);
        api.on('reInit', syncIndex);
        return () => {
            api.off('select', syncIndex);
            api.off('reInit', syncIndex);
        };
    }, [api]);

    return (
        <Carousel setApi={setApi} className={cn('flex w-full flex-col', className)}>
            <div className="relative aspect-video w-full overflow-hidden rounded-2xl [&_[data-slot=carousel-content]]:h-full">
                <Dialog>
                    <CarouselContent className="h-full">
                        {slidesToUse?.map((slide, index) => {
                            const imageContent = (
                                <ParkingImage
                                    image={slide.image ?? imagePlaceholder}
                                    alt={slide.title}
                                    className={cn(
                                        'h-full w-full rounded-2xl object-cover',
                                        hasAtLeastOneImage && 'cursor-pointer',
                                    )}
                                />
                            );

                            return (
                                <CarouselItem key={index} className="h-full">
                                    {hasAtLeastOneImage ? (
                                        <DialogTrigger asChild>
                                            <button
                                                className="block h-full w-full"
                                                onClick={() => setSelectedSlideImage(slide)}
                                                disabled={isLoading}
                                            >
                                                {imageContent}
                                            </button>
                                        </DialogTrigger>
                                    ) : (
                                        imageContent
                                    )}
                                </CarouselItem>
                            );
                        })}
                    </CarouselContent>
                    <DialogContent
                        className="flex size-[90%] h-auto max-w-3xl flex-col overflow-hidden"
                        // Don't return focus to the trigger on close: each slide is its own DialogTrigger and
                        // Radix shares one trigger ref (the last/exit slide wins), so the focus return makes
                        // embla scroll the panel carousel to that slide. The modal must not move the carousel
                        onCloseAutoFocus={(event) => event.preventDefault()}
                    >
                        <DialogHeader className="flex-none">
                            <div className="flex flex-row gap-2">
                                <SquareIcon icon={ICON_MAP.CAMERA} />
                                <div>
                                    <DialogTitle>{selectedSlideImage?.title}</DialogTitle>
                                    <DialogDescription>{selectedSlideImage?.subtitle}</DialogDescription>
                                </div>
                            </div>
                        </DialogHeader>
                        <DialogSeparator />
                        <DialogBody className="flex items-center justify-center">
                            <ImageCarouselInModal
                                imageSlides={slidesToUse}
                                selectedSlide={selectedSlideImage}
                                setSelectedSlide={setSelectedSlideImage}
                            />
                        </DialogBody>
                    </DialogContent>
                </Dialog>
                <div
                    className={cn(
                        'absolute right-0 bottom-0 left-0 flex items-center justify-between rounded-br-2xl rounded-bl-2xl bg-black/50 p-2 text-white',
                        !hasAtLeastTwoImages && 'flex-row justify-center',
                    )}
                >
                    <div className={cn('relative flex items-center justify-center', !hasAtLeastTwoImages && 'hidden')}>
                        <CarouselPrevious
                            imageLength={slidesToUse?.length || 0}
                            className="left-2 cursor-pointer rounded-lg"
                            color="black"
                        />
                    </div>

                    <div className="flex flex-col items-center text-center">
                        <CarouselItemTitle className="text-sm font-medium">
                            {slidesToUse[currentIndex]?.title}
                        </CarouselItemTitle>
                        <CarouselItemSubtitle className="text-xs">
                            {slidesToUse[currentIndex]?.subtitle}
                        </CarouselItemSubtitle>
                    </div>

                    <div className={cn('relative flex items-center justify-center', !hasAtLeastTwoImages && 'hidden')}>
                        <CarouselNext className="right-2 cursor-pointer rounded-lg" color="black" />
                    </div>
                </div>
            </div>
        </Carousel>
    );
};

export default ImageCarousel;
